import React, { FC, useState, useRef, useEffect } from "react";
import { MatterRelationEditor } from "../matter-relation-editor";
import { PhaseList, PhaseListHandle } from "../../phases";
import { TimelineModal, TimelineEditContext } from "./timeline-modal";
import {
  Phase,
  Task,
  Work,
  PricingLevel,
  PricingType,
  OnPhasePriceChange,
  PhaseBase,
} from "../../../data-types";

import { TeamMembershipPartsFragment } from "~/gql/graphql";

type Props = {
  matterId: number;
  template?: boolean;
  title?: string;
  phases: Phase[] | null;
  onUpdate: (phase: PhaseBase) => void;
  onDelete: (id: number) => void;
  onCreate: () => void;
  onCreatePhases: (phases: { phase: string; tasks: string[] }[]) => void;
  onUpdateTask: (task: Partial<Task>, phaseId: number) => void;
  onDeleteTask: (taskId: number, phaseId: number) => void;
  onCreateTask: (phaseId: number) => void;
  onCreateTasks: (taskNames: string[], phaseId: number) => void;
  onDeleteTasks: (phaseId: number) => void;
  onUpdateFeeEarner: (work: Work, phaseId: number, timeEntryUpdate: boolean) => void;
  onDeleteFeeEarner: (workId: number, phaseId: number) => void;
  onCreateFeeEarner: (phaseId: number) => void;
  onReset?: () => void;
  showPriceRangeInput?: boolean;
  pricingLevel: PricingLevel;
  pricingType: PricingType;
  onPriceChange: OnPhasePriceChange;
  onPhaseReorder: (activeId: number, overId: number) => void;
  onTaskReorder: (activeId: number, overId: number) => void;
  teamMembers: TeamMembershipPartsFragment[];
  onAutoFocus: () => void;
  currencySymbol: string;
  countryCode?: string;
  useAi?: boolean;
  defaultFont?: string;
  handleAutoGenerate?: (
    useLibraryData: boolean,
    refineMode: boolean,
    customPrompt: string | null
  ) => void;
  onPhaseListMount?: (
    refineFn: (useLibraryData: boolean, refineMode: boolean, customPrompt: string | null) => void
  ) => void;
};

export const MatterScopeEditor: FC<Props> = ({
  template,
  onCreate,
  useAi,
  onReset,
  defaultFont,
  onPhaseListMount,
  handleAutoGenerate,
  onUpdate,
  onUpdateTask,
  ...others
}) => {
  const [expandedPanels, setExpandedPanels] = useState<string[]>(
    others.phases?.length ? [others.phases[0].id.toString()] : []
  );
  const [customModalVisible, setCustomModalVisible] = useState<boolean>(false);
  const [customPrompt, setCustomPrompt] = useState<string>("");

  const [timelineEdit, setTimelineEdit] = useState<TimelineEditContext | null>(null);

  const handleTimelineChange = (values: {
    startDate?: Date | null;
    deadlineDate?: Date | null;
  }) => {
    if (!timelineEdit) return;

    if (timelineEdit.type === "phase") {
      onUpdate({
        id: timelineEdit.id,
        startDate: values.startDate,
        deadlineDate: values.deadlineDate,
      } as PhaseBase);
    } else {
      onUpdateTask(
        {
          id: timelineEdit.id,
          startDate: values.startDate,
          deadlineDate: values.deadlineDate,
        },
        timelineEdit.phaseId
      );
    }
  };

  const phaseListRef = useRef<PhaseListHandle>(null);

  // This effect allows the parent component to access the handleAutoGenerate function
  // from the PhaseList component. When the parent calls the function provided by
  // onPhaseListMount, it will trigger the handleAutoGenerate method on the PhaseList.
  useEffect(() => {
    if (onPhaseListMount && phaseListRef.current) {
      onPhaseListMount((useLibraryData, refineMode, customPrompt) =>
        phaseListRef.current?.handleAutoGenerate(useLibraryData, refineMode, customPrompt)
      );
    }
  }, [onPhaseListMount]);

  return (
    <>
      <MatterRelationEditor
        template={template}
        workspaceContent={
          <PhaseList
            expandedPanels={expandedPanels}
            setExpandedPanels={setExpandedPanels}
            template={template}
            useAi={useAi}
            defaultFont={defaultFont}
            onTimelineEdit={setTimelineEdit}
            onUpdate={onUpdate}
            onUpdateTask={onUpdateTask}
            ref={phaseListRef}
            setCustomModalVisible={setCustomModalVisible}
            {...others}
          />
        }
        addNew={() => onCreate()}
        showAddNew={true}
        addNewLabel="Add Phase"
        showReset={useAi ? true : false}
        reset={onReset ? () => onReset() : () => {}}
        onExpandAll={() => setExpandedPanels(others.phases?.map((p) => p.id.toString()) ?? [])}
        onCollapseAll={() => setExpandedPanels([])}
        showCollapseExpand={true}
        somePhases={others.phases?.length ? others.phases.length > 0 : false}
        handleAutoGenerate={handleAutoGenerate}
        customModalVisible={customModalVisible}
        setCustomModalVisible={setCustomModalVisible}
        customPrompt={customPrompt}
        setCustomPrompt={setCustomPrompt}
      />
      {timelineEdit && (
        <TimelineModal
          isOpen={true}
          onClose={() => setTimelineEdit(null)}
          mode={timelineEdit.type}
          title={timelineEdit.title || ""}
          startDate={timelineEdit.startDate}
          deadlineDate={timelineEdit.deadlineDate}
          onSave={handleTimelineChange}
        />
      )}
    </>
  );
};
